import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    timeout: 15000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('medprep_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors — only logout on genuine auth failures, not transient issues
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const detail = error.response?.data?.detail || '';
            const isAuthFailure = detail === 'Token expired' || detail === 'Invalid token' || detail === 'User not found' || detail === 'Invalid credentials';
            if (isAuthFailure) {
                localStorage.removeItem('medprep_token');
                localStorage.removeItem('medprep_user');
                const currentPath = window.location.pathname + window.location.search;
                if (currentPath && currentPath !== '/login' && currentPath !== '/') {
                    localStorage.setItem('medprep_return_url', currentPath);
                }
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    getMe: () => api.get('/auth/me'),
    requestPasswordReset: (email) => api.post('/auth/request-password-reset', { email }),
    verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
    resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
    acceptTOU: (locationData) => api.post('/auth/accept-tou', locationData),
};

// Questions API
export const questionsAPI = {
    generate: (subject, difficulty, organSystem = null) => 
        api.post('/questions/generate', { subject, difficulty, organ_system: organSystem }, { timeout: 120000 }),
    getById: (id) => api.get(`/questions/${id}`),
    submitAnswer: async (questionId, data, retries = 2) => {
        for (let i = 0; i <= retries; i++) {
            try {
                return await api.post(`/questions/${questionId}/answer`, data);
            } catch (err) {
                if (i === retries) throw err;
                await new Promise(r => setTimeout(r, 2000));
            }
        }
    },
    getImage: (questionId) => api.get(`/questions/${questionId}/image`),
};

// Analytics API
export const analyticsAPI = {
    getPerformance: () => api.get('/analytics/performance'),
    getMissedPatterns: () => api.get('/analytics/missed-patterns'),
    getMissedQuestions: (subject = null, limit = 20) => {
        const params = new URLSearchParams();
        if (subject) params.append('subject', subject);
        params.append('limit', limit);
        return api.get(`/analytics/missed-questions?${params}`);
    },
    getSpacedRepetition: () => api.get('/analytics/spaced-repetition'),
    resetStats: () => api.post('/analytics/reset-stats'),
};

// CBSE API
export const cbseAPI = {
    getActive: () => api.get('/cbse/active'),
    startExam: (questionCount, timeLimitMinutes) => 
        api.post('/cbse/start', { question_count: questionCount, time_limit_minutes: timeLimitMinutes }),
    getProgress: (examId) => api.get(`/cbse/${examId}/progress`),
    getQuestions: (examId) => api.get(`/cbse/${examId}/questions`),
    saveAnswer: (examId, questionId, selectedAnswer) => 
        api.post(`/cbse/${examId}/answer`, { question_id: questionId, selected_answer: selectedAnswer }),
    submitExam: (examId, answers, timeSpentSeconds) => 
        api.post(`/cbse/${examId}/submit`, { answers, time_spent_seconds: timeSpentSeconds }),
    reviewExam: (examId) => api.get(`/cbse/${examId}/review`),
    abandonExam: (examId) => api.post(`/cbse/${examId}/abandon`),
    getHistory: (limit = 20) => api.get(`/cbse/history?limit=${limit}`),
    getStats: () => api.get('/cbse/stats'),
};

// Admin API
export const adminAPI = {
    getUsers: (skip = 0, limit = 50) => api.get(`/admin/users?skip=${skip}&limit=${limit}`),
    suspendUser: (userId, suspended) => api.post('/admin/users/suspend', { user_id: userId, suspended }),
    makeAdmin: (userId) => api.post(`/admin/users/make-admin?user_id=${userId}`),
    getStats: () => api.get('/admin/stats'),
    setPremium: (userId, durationDays) => api.post('/admin/users/set-premium', { user_id: userId, duration_days: durationDays }),
    setProfessor: (userId, isProfessor) => api.post('/admin/users/set-professor', { user_id: userId, is_professor: isProfessor }),
    resetPassword: (userId, newPassword) => api.post('/admin/users/reset-password', { user_id: userId, new_password: newPassword }),
    exportUsersCsv: () => api.get('/admin/export/users-csv', { responseType: 'blob' }),
    getLibraryContent: () => api.get('/admin/library/content'),
    deleteLibraryContent: (category, groupName, topic = null) => api.post('/admin/library/delete', { category, group_name: groupName, topic }),
    reclassifyLibraryTopic: (topic, oldCategory, oldGroup, newCategory, newGroup) =>
        api.post('/admin/library/reclassify', { topic, old_category: oldCategory, old_group: oldGroup, new_category: newCategory, new_group: newGroup }),
    restoreLibraryTopic: (category, groupName, topic = null) => api.post('/admin/library/restore', { category, group_name: groupName, topic }),
    // Reference Books
    uploadBook: (formData) => api.post('/admin/library/upload-book', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
    }),
    initBookUpload: (filename, fileSize) => api.post(`/admin/library/upload-book/init?filename=${encodeURIComponent(filename)}&file_size=${fileSize}`),
    uploadBookChunk: (uploadId, chunkIndex, formData) => api.post(`/admin/library/upload-book/chunk?upload_id=${uploadId}&chunk_index=${chunkIndex}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
    }),
    completeBookUpload: (uploadId) => api.post(`/admin/library/upload-book/complete?upload_id=${uploadId}`),
    getBooks: () => api.get('/admin/library/books'),
    getBookDiagnostics: () => api.get('/admin/library/books/diagnostics'),
    deleteBook: (bookId) => api.delete(`/admin/library/books/${bookId}`),
    autoGenerateFromBook: (bookId) => api.post(`/admin/library/books/${bookId}/auto-generate`, {}, { timeout: 30000 }),
    enrichLibrary: () => api.post('/admin/library/enrich-all'),
    getEnrichmentStatus: () => api.get('/admin/library/enrichment-status'),
    // Bulk actions
    bulkSuspend: (userIds) => api.post('/admin/bulk/suspend', { user_ids: userIds }),
    bulkUnsuspend: (userIds) => api.post('/admin/bulk/unsuspend', { user_ids: userIds }),
    bulkSetPremium: (userIds, durationDays) => api.post('/admin/bulk/set-premium', { user_ids: userIds, duration_days: durationDays }),
    bulkSetProfessor: (userIds, isProfessor) => api.post('/admin/bulk/set-professor', { user_ids: userIds, is_professor: isProfessor }),
    bulkDelete: (userIds) => api.post('/admin/bulk/delete', { user_ids: userIds }),
    deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
};

// Professor API
export const professorAPI = {
    getQuizzes: () => api.get('/professor/quizzes'),
    getQuiz: (id) => api.get(`/professor/quizzes/${id}`),
    createQuiz: (data) => api.post('/professor/quizzes', data),
    updateQuizStatus: (id, status) => api.patch(`/professor/quizzes/${id}/status?status=${status}`),
    deleteQuiz: (id) => api.delete(`/professor/quizzes/${id}`),
    getQuizResults: (id) => api.get(`/professor/quizzes/${id}/results`),
    getQuizProgress: (id) => api.get(`/professor/quizzes/${id}/progress`),
    getStudents: () => api.get('/professor/students'),
    assignPlan: (data) => api.post('/professor/assign-plan', data),
    autoAssign: (quizId) => api.post(`/professor/auto-assign/${quizId}`),
    getAssignedPlans: () => api.get('/professor/assigned-plans'),
    getAssignedPlanStats: () => api.get('/professor/assigned-plans/stats'),
    deleteAssignedPlan: (id) => api.delete(`/professor/assigned-plans/${id}`),
};

// Public Quiz API (no auth)
export const quizAPI = {
    join: (quizCode, studentName, studentEmail) =>
        api.post('/quiz/join', { quiz_code: quizCode, student_name: studentName, student_email: studentEmail }),
    submit: (attemptId, answers) =>
        api.post('/quiz/submit', { attempt_id: attemptId, answers }),
};

// Study Planner API
export const studyPlanAPI = {
    create: (data) => api.post('/study-plans', data),
    getAll: () => api.get('/study-plans'),
    getById: (planId) => api.get(`/study-plans/${planId}`),
    update: (planId, data) => api.put(`/study-plans/${planId}`, data),
    delete: (planId) => api.delete(`/study-plans/${planId}`),
    startSession: (planId, questionCount, focusSubject = null) => api.post(`/study-plans/${planId}/session`, { question_count: questionCount, focus_subject: focusSubject }, { timeout: 120000 }),
    getPrediction: (planId) => api.get(`/study-plans/${planId}/prediction`),
    getAllPredictions: () => api.get('/study-plans/predictions'),
    getDailyFocus: () => api.get('/study-plans/daily-focus'),
};

// Study Session API
export const studySessionAPI = {
    getQuestion: (sessionId) => api.get(`/study-sessions/${sessionId}/question`, { timeout: 60000 }),
    getSession: (sessionId) => api.get(`/study-sessions/${sessionId}`),
    getMissedQuestions: (sessionId) => api.get(`/study-sessions/${sessionId}/missed`),
    getActive: () => api.get('/study-sessions/active'),
    abandon: (sessionId) => api.delete(`/study-sessions/${sessionId}`),
};

// Quick Review API
export const quickReviewAPI = {
    getStats: () => api.get('/quick-review/stats'),
    start: () => api.post('/quick-review/start'),
    getQuestion: (sessionId) => api.get(`/quick-review/${sessionId}/question`),
    next: (sessionId) => api.post(`/quick-review/${sessionId}/next`),
};

// Weak Areas Analysis API
export const weakAreasAPI = {
    getReport: () => api.get('/analytics/weak-areas'),
};

// Membership API
export const membershipAPI = {
    getStatus: () => api.get('/membership/status'),
    trackTime: (seconds) => api.post(`/membership/track-time?seconds=${seconds}`),
    redeemCode: (code) => api.post('/membership/redeem-code', { code }),
};

// Admin Membership API
export const adminMembershipAPI = {
    createCode: (code, description, durationDays = 365) => api.post('/admin/access-codes', { code, description, duration_days: durationDays }),
    listCodes: () => api.get('/admin/access-codes'),
    deleteCode: (code) => api.delete(`/admin/access-codes/${code}`),
    listMemberships: () => api.get('/admin/memberships'),
};

// Rewards API
export const rewardsAPI = {
    getStatus: () => api.get('/rewards/status'),
    claimMilestone: (milestone) => api.post(`/rewards/claim/${milestone}`),
};

// Lecture Slides API
export const lectureAPI = {
    upload: (formData) => api.post('/lecture-slides/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
    }),
    initUpload: (filename, fileSize, subject, planId, numQuestions) =>
        api.post(`/lecture-slides/upload/init?filename=${encodeURIComponent(filename)}&file_size=${fileSize}${subject ? `&subject=${encodeURIComponent(subject)}` : ''}${planId ? `&plan_id=${planId}` : ''}&num_questions=${numQuestions || 5}`),
    uploadChunk: (uploadSessionId, chunkIndex, formData) =>
        api.post(`/lecture-slides/upload/chunk?upload_session_id=${uploadSessionId}&chunk_index=${chunkIndex}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
        }),
    completeUpload: (uploadSessionId) =>
        api.post(`/lecture-slides/upload/complete?upload_session_id=${uploadSessionId}`),
    uploadWithProgress: async (formData, onUploadProgress, retries = 1) => {
        for (let i = 0; i <= retries; i++) {
            try {
                return await api.post('/lecture-slides/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 300000,
                    onUploadProgress,
                });
            } catch (err) {
                if (i === retries) throw err;
                await new Promise(r => setTimeout(r, 2000));
            }
        }
    },
    getUploads: () => api.get('/lecture-slides'),
    getByPlan: (planId) => api.get(`/lecture-slides/by-plan/${planId}`),
    startPlanSession: (planId, questionCount = null) =>
        api.post(`/lecture-slides/by-plan/${planId}/start-session`, { question_count: questionCount }),
    getStatus: (uploadId) => api.get(`/lecture-slides/${uploadId}/status`),
    getQuestions: (uploadId) => api.get(`/lecture-slides/${uploadId}/questions`),
    getConcepts: (uploadId) => api.get(`/lecture-slides/${uploadId}/concepts`),
    getSummary: (uploadId) => api.get(`/lecture-slides/${uploadId}/summary`, { timeout: 120000 }),
    startSession: (uploadId, questionCount = null) => 
        api.post(`/lecture-slides/${uploadId}/start-session`, { question_count: questionCount }),
    deleteUpload: (uploadId) => api.delete(`/lecture-slides/${uploadId}`),
    linkPlan: (uploadId, planId) => api.patch(`/lecture-slides/${uploadId}/link-plan?plan_id=${planId || ''}`),
    getSessionQuestion: (sessionId) => api.get(`/lecture-sessions/${sessionId}/question`),
    submitAnswer: (sessionId, questionId, answer) => 
        api.post(`/lecture-sessions/${sessionId}/answer`, { question_id: questionId, answer: answer }),
    getExplanation: (questionId) => api.get(`/lecture-questions/${questionId}/explanation`),
    // Learn mode
    getLearnOverview: (uploadId) => api.get(`/lecture-slides/${uploadId}/learn-overview`),
    teachConcept: (uploadId, conceptIndex) => api.post(`/lecture-slides/${uploadId}/teach`, { concept_index: conceptIndex }, { timeout: 120000 }),
    getLessonDiagram: (lessonId) => api.get(`/lecture-lessons/${lessonId}/diagram`),
};

// USMLE Steps API
export const usmleAPI = {
    getSteps: () => api.get('/usmle/steps'),
    startSession: (step, questionCount, subjects = null) => 
        api.post('/usmle/start-session', { step, question_count: questionCount, subjects }),
    getQuestion: (sessionId) => api.get(`/usmle/sessions/${sessionId}/question`),
    submitAnswer: (sessionId, questionId, answer) => 
        api.post(`/usmle/sessions/${sessionId}/answer`, { question_id: questionId, answer: answer }),
};

// Library API
export const libraryAPI = {
    getTopics: () => api.get('/library/topics'),
    getContent: (category, topic) => api.get(`/library/content/${category}/${topic}`, { timeout: 120000 }),
    getRelatedQuestions: (topic) => api.get(`/library/related-questions/${topic}`),
    getHistory: () => api.get('/library/user-history'),
    requestTopic: (category, topicName, description) => 
        api.post('/library/request-topic', { category, topic_name: topicName, description }, { timeout: 120000 }),
    getRequestedTopics: () => api.get('/library/requested-topics'),
    getGenerationStatus: () => api.get('/library/generation-status'),
};

// Shelf Exam API
export const shelfAPI = {
    getSystems: () => api.get('/shelf/systems'),
    startExam: (organSystem, questionCount, timeLimitMinutes, examType = 'system') =>
        api.post('/shelf/start', { organ_system: organSystem, question_count: questionCount, time_limit_minutes: timeLimitMinutes, exam_type: examType }),
    getQuestion: (sessionId) => api.get(`/shelf/${sessionId}/question`),
    submitAnswer: (sessionId, questionId, answer) =>
        api.post(`/shelf/${sessionId}/answer`, { question_id: questionId, answer: answer }),
    submitExam: (sessionId, answers, timeSpentSeconds) =>
        api.post(`/shelf/${sessionId}/submit`, { answers, time_spent_seconds: timeSpentSeconds }),
    getHistory: () => api.get('/shelf/history'),
    getReview: (sessionId) => api.get(`/shelf/${sessionId}/review`),
};


// Anki Export API
export const ankiAPI = {
    getConcepts: () => api.get('/anki/concepts'),
    exportDeck: (concepts, format) => api.post('/anki/export', { concepts, format }, { responseType: 'blob' }),
};

// Study Streak API
export const streakAPI = {
    get: () => api.get('/streak'),
};

export const generalAPI = {
    getSubjects: () => api.get('/subjects'),
    getOrganSystems: () => api.get('/organ-systems'),
    healthCheck: () => api.get('/'),
};

// Admin Announcement API
export const announcementAdminAPI = {
    create: (data) => api.post('/admin/announcements', data),
    list: () => api.get('/admin/announcements'),
    delete: (id) => api.delete(`/admin/announcements/${id}`),
    cancel: (id) => api.patch(`/admin/announcements/${id}/cancel`),
    reactivate: (id) => api.patch(`/admin/announcements/${id}/reactivate`),
};

// User Announcement API
export const announcementAPI = {
    getUnread: () => api.get('/announcements/unread'),
    markRead: (id) => api.post(`/announcements/${id}/read`),
};

// Scratch Pad API
export const scratchpadAPI = {
    create: (data) => api.post('/scratchpads', data),
    list: () => api.get('/scratchpads'),
    get: (id) => api.get(`/scratchpads/${id}`),
    update: (id, data) => api.put(`/scratchpads/${id}`, data),
    delete: (id) => api.delete(`/scratchpads/${id}`),
    regenerateCode: (id) => api.post(`/scratchpads/${id}/regenerate-code`),
    join: (code) => api.post(`/scratchpads/join?code=${code}`),
    getMembers: (id) => api.get(`/scratchpads/${id}/members`),
    updateMember: (padId, userId, permission) => api.put(`/scratchpads/${padId}/members/${userId}`, { permission }),
    removeMember: (padId, userId) => api.delete(`/scratchpads/${padId}/members/${userId}`),
    getNotes: (id, category) => {
        const params = category && category !== 'All' ? `?category=${category}` : '';
        return api.get(`/scratchpads/${id}/notes${params}`);
    },
    createNote: (padId, data) => api.post(`/scratchpads/${padId}/notes`, data),
    updateNote: (padId, noteId, data) => api.put(`/scratchpads/${padId}/notes/${noteId}`, data),
    deleteNote: (padId, noteId) => api.delete(`/scratchpads/${padId}/notes/${noteId}`),
    search: (q) => api.get(`/scratchpads/search?q=${encodeURIComponent(q)}`),
};

export default api;
